import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  archiveDocumentStyle, createDocumentStyle, getListDocumentStylesQueryKey, useListDocumentStyles,
  type DocumentNamedStyle, type DocumentNamedStyleDefinition
} from '@cdf/api-client-react';
import { defaultDocumentStyles, validateDocumentStyles, type DocumentStyle, type DocumentStyles } from '@cdf/api-zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getErrorMessage } from '@/lib/utils';

const PAGE = 10;

function definitionOf(style: DocumentStyle): DocumentNamedStyleDefinition {
  const { fontFamily, fontSize, color, bold, italic, underline, alignment, spaceBefore, spaceAfter, lineHeight } = style;
  return { fontFamily, fontSize, color, bold, italic, underline, alignment, spaceBefore, spaceAfter, lineHeight };
}

/**
 * Entity style library: add shared named styles to this document, or share one of
 * this document's styles. Applying copies the style, so the document never depends on the library.
 */
export function DocumentStyleLibrary(props: {
  styles?: DocumentStyles; disabled: boolean; definitionsDisabled: boolean; onApply: (styles: DocumentStyles) => void;
}) {
  const [open, setOpen] = React.useState(false);
  return <>
    <Button type="button" size="sm" variant="ghost" disabled={props.disabled} onClick={() => setOpen(true)}>Style library</Button>
    {open && <StyleLibraryDialog {...props} onClose={() => setOpen(false)} />}
  </>;
}

/** Mounted only while open so the library is read on demand. */
function StyleLibraryDialog({ styles, disabled, definitionsDisabled, onApply, onClose }: {
  styles?: DocumentStyles; disabled: boolean; definitionsDisabled: boolean; onApply: (styles: DocumentStyles) => void; onClose: () => void;
}) {
  const client = useQueryClient();
  const [search, setSearch] = React.useState(''), [offset, setOffset] = React.useState(0);
  const [share, setShare] = React.useState(''), [busy, setBusy] = React.useState(false);
  const [message, setMessage] = React.useState<string>(), [error, setError] = React.useState<string>();
  const current = styles ?? defaultDocumentStyles();
  const params = { search: search.trim() || undefined, limit: PAGE, offset };
  const query = useListDocumentStyles(params, { query: { queryKey: getListDocumentStylesQueryKey(params), retry: false } });
  const names = new Set(current.definitions.map(style => style.name.trim().toLowerCase()));
  const refresh = () => client.invalidateQueries({ queryKey: getListDocumentStylesQueryKey() });
  async function run(action: () => Promise<string>) {
    setBusy(true); setError(undefined); setMessage(undefined);
    try { setMessage(await action()); } catch (cause) { setError(getErrorMessage(cause)); } finally { setBusy(false); }
  }
  function add(style: DocumentNamedStyle) {
    if (current.definitions.length >= 100) { setError('This document already has the maximum of 100 styles.'); return; }
    const next = { ...current, definitions: [...current.definitions, { ...current.definitions[0]!, ...style.definition, id: 'custom-' + crypto.randomUUID(), name: style.name }] };
    let checked: DocumentStyles;
    try { checked = validateDocumentStyles(next); }
    catch (cause) { setError(getErrorMessage(cause) || 'This style cannot be added to the document.'); return; }
    onApply(checked); setError(undefined); setMessage(`Added “${style.name}” to this document's text styles.`);
  }
  const page = query.data;
  return <>
    <Dialog open onOpenChange={next => { if (!busy && !next) onClose(); }}><DialogContent className="max-h-[90dvh] overflow-y-auto">
      <DialogHeader><DialogTitle>Style library</DialogTitle>
        <DialogDescription>Named text styles shared across your organisation. Adding one copies it into this document, so later library changes never alter saved documents.</DialogDescription></DialogHeader>
      <div className="space-y-4">
        <div className="space-y-1"><Label htmlFor="style-library-search">Find a style</Label>
          <Input id="style-library-search" maxLength={80} value={search} onChange={event => { setSearch(event.target.value); setOffset(0); }} /></div>
        {query.isError ? <p role="alert">The style library is unavailable. <Button variant="link" onClick={() => void query.refetch()}>Retry</Button></p>
          : !page ? <p role="status">Loading styles…</p>
          : page.items.length === 0 ? <p className="text-sm text-muted-foreground">No library styles{search.trim() ? ' match this search' : ' yet'}.</p>
          : <ul className="space-y-2">{page.items.map(style => <li key={style.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-2">
            <span style={{ fontFamily: style.definition.fontFamily, fontSize: Math.min(style.definition.fontSize, 24) + 'pt', color: style.definition.color, fontWeight: style.definition.bold ? 700 : 400, fontStyle: style.definition.italic ? 'italic' : 'normal', textDecoration: style.definition.underline ? 'underline' : 'none' }}>{style.name}</span>
            <span className="flex gap-2">
              <Button size="sm" variant="outline" disabled={disabled || definitionsDisabled || busy || names.has(style.name.trim().toLowerCase())} onClick={() => add(style)}>{names.has(style.name.trim().toLowerCase()) ? 'In this document' : 'Add to document'}</Button>
              {page.canEdit && <Button size="sm" variant="ghost" disabled={busy} onClick={() => { if (window.confirm(`Archive “${style.name}”? Documents that already use it keep their copy.`)) void run(async () => { await archiveDocumentStyle(style.id, { expectedRevision: style.revision }); await refresh(); return `Archived “${style.name}”.`; }); }}>Archive</Button>}
            </span>
          </li>)}</ul>}
        {page && page.total > PAGE && <div className="flex items-center gap-2 text-sm">
          <Button size="sm" variant="outline" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</Button>
          <span>{offset + 1}–{Math.min(offset + PAGE, page.total)} of {page.total}</span>
          <Button size="sm" variant="outline" disabled={offset + PAGE >= page.total} onClick={() => setOffset(offset + PAGE)}>Next</Button>
        </div>}
        {page?.canEdit && <form className="space-y-2 rounded-md border p-3" onSubmit={event => {
          event.preventDefault();
          const style = current.definitions.find(item => item.id === share);
          if (!style) return;
          void run(async () => { const saved = await createDocumentStyle({ name: style.name.trim(), definition: definitionOf(style) }); setShare(''); await refresh(); return `Saved “${saved.name}” to the style library.`; });
        }}>
          <Label htmlFor="style-library-share">Share one of this document's styles</Label>
          <select id="style-library-share" className="h-9 w-full rounded-md border bg-background px-2 text-sm" value={share} onChange={event => setShare(event.target.value)}>
            <option value="">Choose a style</option>{current.definitions.map(style => <option key={style.id} value={style.id}>{style.name}</option>)}
          </select>
          <Button type="submit" size="sm" disabled={busy || !share}>Save to library</Button>
        </form>}
        {message && <p role="status" className="text-sm">{message}</p>}
        {error && <p role="alert">{error}</p>}
      </div>
    </DialogContent></Dialog>
  </>;
}
